import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'

import {
  Configuration,
  UserPrivate,
  ConfigurationParameters,
  AccountsApi,
  APITokensApi,
  UserApi,
  UsersApi,
  OrgsApi,
  TeamsApi,
  PluginsApi,
  RecipesApi,
  ProjectsApi,
  RunsApi,
  JobsApi,
  ArtifactsApi,
  RegistriesApi,
  SubscriptionsApi,
  LicensesApi,
  SubscriptionPlansApi,
  ApplicationsApi,
} from '@pollination-solutions/pollination-sdk'

import { useToken } from './useToken'
import { useApiKey } from './useApiKey'

export class APIClient {
  config: Configuration
  accounts: AccountsApi
  user: UserApi
  apiKeys: APITokensApi
  users: UsersApi
  orgs: OrgsApi
  teams: TeamsApi
  plugins: PluginsApi
  recipes: RecipesApi
  registries: RegistriesApi
  projects: ProjectsApi
  jobs: JobsApi
  runs: RunsApi
  artifacts: ArtifactsApi
  subscriptionPlans: SubscriptionPlansApi
  subscriptions: SubscriptionsApi
  licenses: LicensesApi
  applications: ApplicationsApi

  constructor(props: ConfigurationParameters) {
    this.config = new Configuration(props)
    this.accounts = new AccountsApi(this.config)
    this.user = new UserApi(this.config)
    this.apiKeys = new APITokensApi(this.config)
    this.users = new UsersApi(this.config)
    this.orgs = new OrgsApi(this.config)
    this.teams = new TeamsApi(this.config)
    this.plugins = new PluginsApi(this.config)
    this.recipes = new RecipesApi(this.config)
    this.registries = new RegistriesApi(this.config)
    this.projects = new ProjectsApi(this.config)
    this.jobs = new JobsApi(this.config)
    this.runs = new RunsApi(this.config)
    this.artifacts = new ArtifactsApi(this.config)
    this.subscriptionPlans = new SubscriptionPlansApi(this.config)
    this.subscriptions = new SubscriptionsApi(this.config)
    this.licenses = new LicensesApi(this.config)
    this.applications = new ApplicationsApi(this.config)
  }

  private init(): void {
    this.accounts = new AccountsApi(this.config)
    this.user = new UserApi(this.config)
    this.apiKeys = new APITokensApi(this.config)
    this.users = new UsersApi(this.config)
    this.orgs = new OrgsApi(this.config)
    this.teams = new TeamsApi(this.config)
    this.plugins = new PluginsApi(this.config)
    this.recipes = new RecipesApi(this.config)
    this.registries = new RegistriesApi(this.config)
    this.projects = new ProjectsApi(this.config)
    this.jobs = new JobsApi(this.config)
    this.runs = new RunsApi(this.config)
    this.artifacts = new ArtifactsApi(this.config)
    this.subscriptionPlans = new SubscriptionPlansApi(this.config)
    this.subscriptions = new SubscriptionsApi(this.config)
    this.licenses = new LicensesApi(this.config)
    this.applications = new ApplicationsApi(this.config)
  }

  /** Overwrite current configuration and re-create all api clients. */
  configure(config: Partial<ConfigurationParameters>): void {
    this.config = new Configuration(config)
    this.init()
  }
}

const basePath = process.env.REACT_APP_API_BASE_PATH || 'https://api.pollination.cloud'

export const useAuthenticatedApiClient = (baseConfig?: ConfigurationParameters) => {
  const jwtToken = useToken()
  const apiKey = useApiKey()

  const [config, setConfig] = useState<ConfigurationParameters>(baseConfig)
  const [error, setError] = useState('')

  useEffect(() => {
    if (jwtToken.data && !jwtToken.error) {
      setConfig({ ...baseConfig, accessToken: jwtToken.data })
    } else if (apiKey.data && !apiKey.error) {
      setConfig({ ...baseConfig, apiKey: apiKey.data })
    } else if (!baseConfig.accessToken && !baseConfig.apiKey) {
      setConfig(baseConfig)
      setError('Authentication failed.')
    }
  }, [jwtToken, apiKey, baseConfig])

  const { client, authUser, loading } = useAPIClient(config)

  return {
    client,
    authUser,
    loading,
    error,
  }
}

export const useAPIClient = (config?: ConfigurationParameters) => {
  const [authUser, setAuthUser] = useState<UserPrivate>()
  const [loading, setLoading] = useState(false)

  const client = useRef<APIClient>(new APIClient({ basePath, ...config }))

  useEffect(() => {
    if (!config) return

    client.current.configure({ basePath, ...config })

    setLoading(true)

    client.current.user.getMe()
      .then(({ data }) => setAuthUser(data))
      .catch(() => { setLoading(false) })
      .finally(() => setLoading(false))
  }, [config])

  return {
    client,
    authUser,
    loading,
  }
}

export default useAPIClient
